import { useState } from 'react';
import { CardActions, Collapse, Grid, Paper, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { $danger, $elevated, $inactive, $primary, $success } from 'styles/colors';
import { ReactComponent as DistanceIcon } from 'assets/icons/distance.svg';
import NoAdvisories from 'components/rightPanel/searchAndAdvisories/advisories/NoAdvisories';
import { determineIfLAANCEnabled, formatAdvisoryDetailKeyAndValue } from 'utils/advisoriesUtils';
import ShareModal from 'components/rightPanel/searchAndAdvisories/advisories/ShareModal';
import AdvisoryInfoPopup from 'components/rightPanel/searchAndAdvisories/advisories/AdvisoryInfoPopup';
import GetLAANCModal from 'components/rightPanel/searchAndAdvisories/advisories/GetLAANCModal';
import { logEvent } from 'actions/firebase';
import { CARD_COLLAPSE, CARD_EXPAND } from 'constants/firebase';

const styles = {
	advisoriesList: {
		padding: 20,
		height: 'calc(100% - 408px)',
		overflowY: 'auto'
	},
	root: {
		justifyContent: 'flex-start',
		flexWrap: 'wrap',
		position: 'relative'
	},
	paper: {
		margin: 'auto'
	},
	card: {
		margin: '20px 0px',
		width: '100%'
	},
	firstCard: {
		'&:first-child': {
			marginTop: 0
		}
	},
	lastCard: {
		'&:last-child': {
			marginBottom: 0
		}
	},
	cardContainer: {
		padding: '5px 15px !important',
		margin: 0,
		width: '100%'
	},
	red: {
		borderLeft: `${$danger} solid 7px`
	},
	blue: {
		borderLeft: `${$primary} solid 7px`
	},
	yellow: {
		borderLeft: `${$elevated} solid 7px`
	},
	green: {
		borderLeft: `${$success} solid 7px`
	},
	grey: {
		borderLeft: `${$inactive} solid 7px`
	},
	title: {
		paddingTop: 7,
		fontWeight: '600',
		fontSize: '1rem'
	},
	subtitle: {
		fontSize: '.9rem',
		color: '#495057',
		wordWrap: 'break-word',
		fontStyle: 'italic',
		lineHeight: '1.3',
		minHeight: 20,
		whiteSpace: 'pre-wrap'
	},
	svg: {
		width: '15px'
	},
	pinDetails: {
		paddingLeft: '5px',
		fontSize: '17px',
		fontWeight: '600'
	},
	pinDetailContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'baseline'
	},
	cardRightContainer: {
		width: '100%',
		justifyContent: 'flex-end'
	},
	actions: {
		padding: 0,
		alignSelf: 'end',
		justifyContent: 'flex-end',
		width: '100%'
	},
	expandedDetailContainer: {
		width: '100%',
		marginTop: 20
	},
	expandedDetails: {
		display: 'grid',
		gridTemplateColumns: '4fr 8fr',
		marginBottom: 5
	},
	expandedDetailValue: {
		fontWeight: 600
	},
	actionsContainer: {
		position: 'relative',
		display: 'flex'
	},
	titleContainer: {
		display: 'flex',
		justifyContent: 'space-between'
	}
};

const AdvisoriesList = ({ advisories }) => {
	const useStyles = makeStyles(styles);
	const classes = useStyles();
	const [expanded, setExpanded] = useState([]);

	const handleExpandClick = index => {
		let updatedExpanded = [...expanded];
		const alreadyExpandedIndex = expanded.indexOf(index);
		if (alreadyExpandedIndex >= 0) {
			logEvent(CARD_COLLAPSE);
			updatedExpanded.splice(alreadyExpandedIndex, 1);
		} else {
			logEvent(CARD_EXPAND);
			updatedExpanded.push(index);
		}
		setExpanded(updatedExpanded);
	};

	return (
		<>
			{advisories.length === 0 && <NoAdvisories />}
			{advisories.length > 0 && (
				<div className={classes.advisoriesList}>
					{advisories.map((item, index) => {
						const {
							name,
							description,
							color,
							distance: { value, contains },
							details,
							type
						} = item;
						const laancEnabled = determineIfLAANCEnabled(item);

						let hasDetails = details.length;
						let distanceFrom;

						if (!value) {
							distanceFrom = '';
						} else if (value * 3.28084 <= 100) {
							distanceFrom = `${(value * 3.28084).toFixed(0)}ft`;
						} else {
							distanceFrom = `${(value * 0.000621371).toFixed(2)}mi`;
						}

						return (
							<div
								key={index}
								className={[classes.card, classes.firstCard, classes.lastCard].join(' ')}
							>
								<Paper
									className={[classes[color.name], classes.cardContainer, classes.paper].join(' ')}
								>
									<Grid container>
										<Grid item xs={12}>
											<div className={classes.titleContainer}>
												<Typography variant='h6' className={classes.title}>
													{name}
												</Typography>
												<div className={classes.actionsContainer}>
													<ShareModal name={name} type={type} />
													<AdvisoryInfoPopup advisory={item} />
												</div>
											</div>
										</Grid>
										<Grid item xs={12}>
											<Typography variant='h6' className={classes.subtitle}>
												{description}
											</Typography>
										</Grid>
										{laancEnabled && (
											<Grid item xs={12}>
												<GetLAANCModal />
											</Grid>
										)}
										<Grid item xs={12} className={classes.cardRightContainer}>
											{!contains && distanceFrom && (
												<Typography className={classes.pinDetailContainer}>
													<DistanceIcon />
													<span className={classes.pinDetails}>{distanceFrom}</span>
												</Typography>
											)}
											<Collapse
												className={classes.expandedDetailContainer}
												in={expanded.includes(index)}
												timeout='auto'
												unmountOnExit
											>
												{details.map((item, idx) => {
													const { value, key } = formatAdvisoryDetailKeyAndValue(item);
													if (key && value) {
														return (
															<section key={idx}>
																<Typography className={classes.expandedDetails} paragraph>
																	<span>{`${key}:`}</span>
																	<span className={classes.expandedDetailValue}>{value}</span>
																</Typography>
															</section>
														);
													} else {
														return null;
													}
												})}
											</Collapse>
											{hasDetails && (
												<CardActions className={classes.actions}>
													<Typography
														key={index}
														onClick={() => handleExpandClick(index)}
														aria-expanded={expanded.includes(index)}
														aria-label='Show more'
														style={{ cursor: 'pointer' }}
														color='primary'
													>
														{expanded.includes(index) ? 'less' : 'more'}
													</Typography>
												</CardActions>
											)}
										</Grid>
									</Grid>
								</Paper>
							</div>
						);
					})}
				</div>
			)}
		</>
	);
};

export default AdvisoriesList;
